import { globalVariables } from "helper/globalVariables";
import Layout from "shared/Layout";
import { globalClasses } from "helper/globalClasses";
import { deleteIcon, uploadCloud } from "helper/icons";
import { useQuery } from "@apollo/client";
import {
  DeleteImageMutation,
  GetAllImages,
} from "redux/features/superAdminSlice/manageGallery";
import Loader from "shared/Components/loader";
import Table from "shared/Components/table";
import { OutlineButton } from "shared/Components/outlineButton";
import { client } from "helper/apolloConfig";
import { useRef, useState } from "react";
import { MobileGalleryComponent } from "./components/MobileGallery";
import { useUpdateImageMutation } from "redux/features/api/apiSlice";
import { PopUpMessages } from "shared/Components/popUpMessages";
import PageInfo from "shared/Components/pageInfo";

const ImageRow = ({ data, index, callBack }) => {
  const [updateImage, { isLoading: updateImageLoading }] =
    useUpdateImageMutation();

  const inputRef = useRef(null);
  const modalRef = useRef();
  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });

  const [isLoading, setLoading] = useState(false);
  const { imageName, imageKey, imageUrl } = data;
  const { tableDataCls } = globalClasses;

  const onCloseModal = () => {};

  const onDelete = async (imageKey) => {
    setLoading(true);
    const result = await client.mutate({
      mutation: DeleteImageMutation,
      variables: { imageKey: imageKey },
    });
    setLoading(false);
    if (result.data) {
      callBack();
    }
  };
  const handleFileChange = (e) => {
    if (e.target.files?.length) {
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (e.target.files[0].size <= maxSize) {
        const formdata = new FormData();
        formdata.append("image", e.target.files[0]);
        formdata.append("imageKey", imageKey);
        updateImage(formdata).then(() => {
          callBack();
        });
      } else {
        setApiData({
          title: "Warning",
          desc: globalVariables.maximumFileSizeAllowed, // Updated property access
          type: "pending",
        });
        modalRef.current.openModal();
      }
    }
  };

  return (
    <>
      <tr>
        <td className={`${tableDataCls} min-w-[20px] w-[20px]`}>
          {String(index + 1).padStart(2, "0")}
        </td>
        <td
          onClick={() => imageUrl && window.open(imageUrl, "_blank")}
          className={`${tableDataCls} px-0 text-start min-w-[50%] ${ imageUrl && 'cursor-pointer'} w-[50%]`}
        >
          {imageName !== "" ? imageName : globalVariables.uploadImage}
        </td>
        <td className={`${tableDataCls} flex justify-center border-b-0`}>
          <OutlineButton
            width={"w-[140px]"}
            label={globalVariables.upload}
            labelClass="!text-primary"
            icon={uploadCloud}
            onClick={() => {
              inputRef.current.click();
            }}
            loading={updateImageLoading}
          />
          <input
            type="file"
            className="hidden"
            id="upload"
            ref={inputRef}
            accept={"image/png, image/jpg, image/jpeg"}
            onChange={handleFileChange}
            onClick={(event) => {
              event.target.value = null;
            }}
            hidden
          />
        </td>
        <td className={`${tableDataCls} min-w-[20px] w-[20px]`}>
          {imageName !== "" ? (
            isLoading ? (
              <Loader customStyle={{ margin: 0 }} />
            ) : (
              <div
                className="cursor-pointer"
                onClick={() => onDelete(imageKey)}
              >
                <img src={deleteIcon} alt="deleteIcon" />
              </div>
            )
          ) : (
            <></>
          )}
        </td>
      </tr>
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={modalRef}
        onCloseModal={onCloseModal}
      />
    </>
  );
};

const ManageGallery = () => {
  const { data, loading, refetch } = useQuery(GetAllImages);

  const renderTableBody = () => {
    return data?.getAllImageGallery?.map((item, index) => (
      <ImageRow
        data={item}
        key={item.id}
        index={index}
        callBack={() => {
          refetch();
        }}
      />
    ));
  };

  const HeaderComponent = () => {
    return (
      <tr className="bg-purple-300/70">
        <th className="text-center w-[8%] text-center py-3 text-xs font-medium text-gray-600">
          {globalVariables.numberLabel}
        </th>
        <th className="text-left w-[50%] py-3 px-6 text-xs font-medium text-gray-600">
          {globalVariables.imageName}
        </th>
        <th className="text-center w-[30%] py-3 text-xs font-medium text-gray-600">
          {globalVariables.upload}
        </th>
        <th className="text-center w-[18px] py-3 text-xs font-medium text-gray-600"></th>
      </tr>
    );
  };

  const GalleryMobileListing = () => {
    return (
      <div className="mt-[20px]">
        {loading ? (
          <Loader />
        ) : (
          <div
            className={`pb-[12px] ${
              data?.getAllImageGallery?.length === 0 ? "w-full text-center" : ""
            }`}
          >
            {data?.getAllImageGallery?.length > 0 ? (
              data?.getAllImageGallery?.map((item, index) => (
                <MobileGalleryComponent
                  key={item.id}
                  data={item}
                  index={index}
                  callBack={() => refetch()}
                />
              ))
            ) : (
              <span className="text-center text-gray-500">
                {globalVariables.noDataAvailable}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`App lg:rounded-md min-h-screen lg:ml-[17rem] sm:pt-8 bg-white lg:p-10`}>
      <PageInfo title={globalVariables.manageGallery}/>
      <div className={`mt-[20px] md:hidden`}>
        <div className="max-w-md mx-auto max-[450px]:px-5">
          <span className="font-semibold text-gray-900">
            {globalVariables.detailOfImages}
          </span>
        </div>
        <GalleryMobileListing />
      </div>
      <div className="hidden md:block md:mt-[16px] lg:block lg:mt-8">
        {loading ? (
          <Loader />
        ) : (
          <Table
            HeadComponent={HeaderComponent}
            tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[calc(100vh-9rem)]"}
            title={globalVariables.detailOfImages}
            tableBody={renderTableBody()}
          />
        )}
      </div>
    </div>
  );
};
export default ManageGallery;
